/**
 * V2 tutorial checklist — V1's 12-step Getting Started list
 * (components/tutorial-checklist.js) as a collapsible card on Discover.
 * Same step ids, same localStorage key, same server sync
 * (set_tutorial_progress → profiles.tutorial_progress), so progress
 * carries between V1 and V2. Coach-mark pulses are V1-only for now.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { icon } from '../../ui/icons.js';

const STORAGE_KEY = 'yoink_tutorial_progress'; // shared with V1

export const STEPS = [
  { id: 'browse_categories', group: 'Discover', label: 'Browse categories' },
  { id: 'follow_category', group: 'Discover', label: 'Follow a category', login: true },
  { id: 'view_category', group: 'Discover', label: 'Visit a category page' },
  { id: 'switch_view', group: 'Discover', label: 'Switch between Grid and Map' },
  { id: 'view_yoink', group: 'Discover', label: 'Open a yoink' },
  { id: 'go_home', group: 'Discover', label: 'Go home — reset and start fresh' },
  { id: 'switch_scope', group: 'Discover', label: 'Switch All / Following', login: true },
  { id: 'register', group: 'Log', label: 'Sign in' },
  { id: 'log_yoink', group: 'Log', label: 'Log your first yoink', login: true },
  { id: 'share', group: 'Share', label: 'Share a yoink' },
  { id: 'share_profile', group: 'Share', label: 'Share your journal', login: true },
  { id: 'open_how_it_works', group: 'Share', label: 'Find this checklist again' },
];

let progress = null;
let syncTimer = null;
const listeners = new Set();
let revealed = false;

function load() {
  if (progress) return progress;
  try {
    progress = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    progress = {};
  }
  return progress;
}

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // private mode — session only
  }
}

/** Merge server progress (profiles.tutorial_progress) — server wins per V1. */
export function mergeServerProgress(serverProgress) {
  if (!serverProgress || typeof serverProgress !== 'object') return;
  const local = load();
  if (!Object.values(serverProgress).some(Boolean)) {
    // Empty server progress while login-gated steps are complete locally
    // can only mean an admin reset: those steps need a session, and
    // completing one syncs to the server. Server wins — clear this device
    // too, so a reset reaches every browser on its next profile load.
    // A fresh signup with anonymous local progress has no login-gated
    // steps yet, so pre-signup checkmarks survive.
    if (STEPS.some((s) => s.login && local[s.id]) || local.register) {
      progress = {};
      save();
      clearTimeout(syncTimer);
      syncTimer = null;
      notify();
    }
    return;
  }
  progress = { ...local, ...serverProgress };
  save();
  notify();
  // Self-heal divergence: when this device knows more than the server
  // (a debounced sync died with the tab — see the visibility flush
  // below), push the richer local state back up on profile load.
  const serverCount = STEPS.filter((s) => serverProgress[s.id]).length;
  if (completedCount() > serverCount) scheduleSync();
}

export function completedCount() {
  const p = load();
  return STEPS.filter((s) => p[s.id]).length;
}

/** Mark a step complete; syncs to the server like V1 (debounced). */
export function markComplete(stepId) {
  const p = load();
  if (p[stepId]) return;
  p[stepId] = true;
  save();
  if (completedCount() === STEPS.length) {
    // V1 animated the card out on the last step; V2 celebrates the way
    // its milestones do
    showToast('Getting Started complete — you’ve mastered the field journal!', {
      type: 'celebrate', duration: 9000,
    });
  }
  notify();
  scheduleSync();
}

/** Steps that complete by ARRIVING at a route (V1 completed them on the
 * corresponding tap — logo, Discover button, card taps). */
const ROUTE_STEPS = {
  discover: 'go_home',
  categories: 'browse_categories',
  category: 'view_category',
  yoink: 'view_yoink',
};

/**
 * Route-based completions, called on every route change. The LANDING
 * route is not an action the user took inside the app: marking go_home
 * on arrival (every visit starts at Discover) — or view_yoink on a
 * shared deep link — pre-checks steps the user never did, and a fresh
 * visitor whose count isn't 0 gets a COLLAPSED checklist instead of
 * the expanded welcome (checklistHtml only starts open at 0 done).
 */
export function markRouteComplete(routeName, { initial = false } = {}) {
  if (initial) return;
  const step = ROUTE_STEPS[routeName];
  if (step) markComplete(step);
}

function pushSync() {
  syncTimer = null;
  // Fire-and-forget: it can outlive the page (or, in vitest, the test
  // file whose gateway mock lacks isInitialized — the mock proxy throws
  // on the property ACCESS, which randomly failed CI deploys through
  // 2026-07-17).
  try {
    if (!gateway.isInitialized()) return;
    // p_count is REQUIRED — the function signature is
    // (p_count smallint, p_progress jsonb) with no default on p_count,
    // so omitting it makes every sync fail silently (M8.5 Tier 0).
    // Read fresh state at fire time: more steps may have completed
    // during the debounce window.
    gateway.rpc('set_tutorial_progress', {
      p_count: completedCount(),
      p_progress: load(),
    }).catch?.(() => {});
  } catch { /* background sync — never let it take anything down */ }
}

function scheduleSync() {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(pushSync, 1500);
}

// A debounced sync dies silently when the tab suspends inside the 1.5s
// window (iPad Safari especially) — and once every step is complete no
// later markComplete re-sends, so the server lags behind this device
// forever (admin showed 4/12 against a local 12/12, 2026-07-22). Flush
// the pending sync the moment the page hides.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && syncTimer) {
      clearTimeout(syncTimer);
      pushSync();
    }
  });
}

/**
 * The collapsible Getting Started card. Signed-OUT visitors see it too
 * (V1 tutorial-checklist.js:317-334) — login-gated steps wear a lock
 * until there's a session.
 *
 * INVITED ONLY since 2026-07-27. It used to open Discover for anyone with
 * an unfinished list, which cost the feed a band on every visit for as long
 * as a step went untouched. The nav's Help button carries the invitation
 * instead (see `tutorialPending`), and Help → Tutorial reveals this.
 */
export function checklistHtml(signedIn = true) {
  const p = load();
  const done = completedCount();
  if (!revealed) return '';
  const groups = ['Discover', 'Log', 'Share'];
  // Progress re-renders rebuild the card — keep whatever open/closed
  // state the user left it in (fresh users start expanded)
  const wasOpen = document.querySelector('.tutorial-card')?.open ?? (done === 0 || revealed);
  return `
    <details class="tutorial-card" ${wasOpen ? 'open' : ''}>
      <summary>Getting started <span class="geo-count">${done}/${STEPS.length}</span>
        ${done >= STEPS.length ? `
        <button type="button" class="tutorial-card-close" data-tut-close
          aria-label="Hide the checklist">&times;</button>` : ''}
      </summary>
      <p class="tutorial-hint">Tap any step for a lesson — it’ll show you right where to go.</p>
      ${groups.map((g) => `
        <div class="tutorial-group">
          <h4>${g}</h4>
          <ul>
            ${STEPS.filter((s) => s.group === g).map((s) => `
              <li class="${p[s.id] ? 'tut-done' : ''}">
                <button type="button" class="tutorial-step-btn" data-tut-step="${s.id}"
                  aria-label="${p[s.id] ? 'Done: ' : ''}${escapeHtml(s.label)}">
                  <span aria-hidden="true">${p[s.id] ? '✓' : s.login && !signedIn ? icon('lock', 12) : '·'}</span>
                  ${escapeHtml(s.label)}
                </button>
              </li>`).join('')}
          </ul>
        </div>`).join('')}
    </details>`;
}

/**
 * Subscribe to progress changes. A SET, not a single slot (2026-07-27):
 * the nav's Help button now lights up while the checklist is unfinished,
 * and it subscribes alongside Discover — a single slot meant whichever
 * mounted last silently silenced the other.
 * @returns {() => void} unsubscribe
 */
/** Is the checklist unfinished? Drives the nav's lit Help button. */
export function tutorialPending() {
  return completedCount() < STEPS.length;
}

export function onProgressChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  listeners.forEach((fn) => fn());
}

/**
 * Help → Tutorial: show the checklist even when it's complete, so
 * finished users can re-open any lesson (V1's How-It-Works modal
 * listed completed items too). Sticky for the rest of the session.
 */
export function revealChecklist() {
  revealed = true;
  notify();
}

/** The ✕ on a revealed, completed checklist — put it away again. */
export function hideChecklist() {
  revealed = false;
  notify();
}

/**
 * Clear all progress, including localStorage (V1 tutorial-checklist.js
 * resetProgress) — called when an admin resets their own account so the
 * checklist reappears immediately. Cancels any pending sync so stale
 * local progress can't be pushed back to the just-reset server row.
 */
export function resetProgress() {
  progress = {};
  save();
  clearTimeout(syncTimer);
  syncTimer = null;
  notify();
}

/** Test hook. */
export function _reset() {
  progress = null;
  listeners.clear();
  revealed = false;
  clearTimeout(syncTimer);
  syncTimer = null;
}
